import { ComponentPropsWithoutRef, forwardRef } from "react";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import cx from "classnames";

import { Text } from "../Text";
import { DialogCancelButton } from "./DialogCancelButton";

import styles from "./Dialog.module.scss";

/**
 * Props for the DialogHeader component
 * @extends ComponentPropsWithoutRef<"header">
 */
export type DialogHeaderProps = ComponentPropsWithoutRef<"header">;

/**
 * DialogHeader component for the header section of a Dialog.
 *
 * Features:
 * - Displays dialog title with headline typography
 * - Includes close button with proper accessibility
 * - Sticky positioning for long content
 * - Consistent spacing and styling
 * - Automatic focus management
 *
 * @example
 * <Dialog.Header>Confirm Delete</Dialog.Header>
 */
export const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(
  (props, ref) => {
    const { children, className, ...rest } = props;

    const headerClassNames = cx(className, styles["header"]);

    return (
      <header
        className={headerClassNames}
        data-anv="dialog-header"
        ref={ref}
        {...rest}
      >
        <Text variant="headline" el="h1">
          {children}
        </Text>
        <span className={styles["close-container"]}>
          <DialogCancelButton
            className={styles["close-button"]}
            size="small"
            appearance="ghost"
            aria-label="Close dialog"
            icon={Close}
          />
        </span>
      </header>
    );
  },
);

DialogHeader.displayName = "DialogHeader";
